import React, { useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { useMutation, useQuery } from "@apollo/client";
import { CREATE_DYNAMIC_FORM, UPDATE_DYNAMIC_FORM } from "../../graphql/mutations";
import { GET_DYNAMIC_FORM } from "../../graphql/queries";
import "./FormStyles.css"; // Scoped CSS for DynamicReport and FormPage

function DynamicReport({ formId }) {
  const [formName, setFormName] = useState("New Report");
  const [cards, setCards] = useState([]);

  // Fetch an existing form if `formId` is provided
  const { loading, error, data } = useQuery(GET_DYNAMIC_FORM, {
    variables: { id: formId },
    skip: !formId,
    fetchPolicy: "network-only",
  });

  // Mutations
  const [createForm] = useMutation(CREATE_DYNAMIC_FORM);
  const [updateForm] = useMutation(UPDATE_DYNAMIC_FORM);

  // On fetch success, populate state
  useEffect(() => {
    if (data && data.getDynamicForm) {
      setFormName(data.getDynamicForm.name);
      setCards(data.getDynamicForm.cards || []);
    }
  }, [data]);

  // -----------------------
  // Insert a new card above or below a given card index
  // -----------------------
  const addCardAtIndex = (baseIndex, position = "below") => {
    const newCard = {
      id: uuidv4(),
      label: "Untitled Section",
      pageIndex: 1,
      fields: [],
    };

    const updated = [...cards];
    const insertIndex = position === "above" ? baseIndex : baseIndex + 1;
    updated.splice(insertIndex, 0, newCard);
    setCards(updated);
  };

  // Basic addCard (appends at the end) - unchanged
  const addCard = () => {
    addCardAtIndex(cards.length, "below");
  };

  // Update a card's label
  const updateCardLabel = (cardIndex, newLabel) => {
    const updated = [...cards];
    updated[cardIndex].label = newLabel;
    setCards(updated);
  };

  // Update the page index
  const updateCardPage = (cardIndex, newPage) => {
    const updated = [...cards];
    updated[cardIndex].pageIndex = newPage;
    setCards(updated);
  };

  // Remove a card by index
  const removeCard = (cardIndex) => {
    setCards(cards.filter((_, i) => i !== cardIndex));
  };

  // ------------------------
  // Field-level operations
  // ------------------------
  const addField = (cardIndex, fieldType) => {
    const updated = [...cards];
    updated[cardIndex].fields.push({
      id: uuidv4(),
      label: "",
      type: fieldType,
      options: fieldType === "select" || fieldType === "radio" ? [] : [],
    });
    setCards(updated);
  };

  const updateField = (cardIndex, fieldIndex, key, value) => {
    const updated = [...cards];
    updated[cardIndex].fields[fieldIndex][key] = value;
    setCards(updated);
  };

  const removeField = (cardIndex, fieldIndex) => {
    const updated = [...cards];
    updated[cardIndex].fields.splice(fieldIndex, 1);
    setCards(updated);
  };

  // Save or update the form
  const saveReport = async () => {
    try {
      if (formId) {
        // Update existing form
        await updateForm({
          variables: {
            input: {
              id: formId,
              name: formName,
              cards: cards,
            },
          },
        });
        alert("Form updated successfully!");
      } else {
        // Create new form
        const result = await createForm({
          variables: {
            input: {
              name: formName,
              cards: cards,
            },
          },
        });
        alert("Form created successfully!");
        console.log("Created form:", result.data.createDynamicForm);
      }
    } catch (err) {
      console.error("Error saving form:", err);
    }
  };

  if (loading) return <p>Loading form...</p>;
  if (error) return <p>Error loading form: {error.message}</p>;

  return (
    <div className="dynamic-report-root">
      <div className="dynamic-report-container">
        <header className="dynamic-report-header">
          <h1>{formId ? "Edit Report" : "Create Report"}</h1>
        </header>

        {/* Form Name */}
        <div className="report-name-section">
          <label>Report Name:</label>
          <input
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
          />
        </div>

        {/* Cards */}
        {cards.map((card, cardIndex) => (
          <div key={card.id} className="card-section">
            {/* Buttons that appear on hover at the top of the card */}
            <div className="card-hover-buttons top-buttons">
              {/* Add Card Above */}
              <button
                className="hover-button"
                onClick={() => addCardAtIndex(cardIndex, "above")}
              >
                + Card Above
              </button>
              {/* Remove This Card */}
              <button
                className="hover-button remove-card-hover"
                onClick={() => removeCard(cardIndex)}
              >
                Remove
              </button>
            </div>

            <input
              className="card-label-input"
              type="text"
              value={card.label}
              onChange={(e) => updateCardLabel(cardIndex, e.target.value)}
              placeholder="Section Title"
            />
            <label>Page:</label>
            <input
              type="number"
              min="1"
              value={card.pageIndex || 1}
              onChange={(e) =>
                updateCardPage(cardIndex, parseInt(e.target.value, 10))
              }
              className="card-page-input"
            />

            {/* Fields inside each card */}
            {card.fields.map((field, fieldIndex) => (
              <div className="field-editor" key={field.id}>
                <label>Field Label:</label>
                <input
                  type="text"
                  value={field.label}
                  onChange={(e) =>
                    updateField(cardIndex, fieldIndex, "label", e.target.value)
                  }
                />

                <label>Type:</label>
                <select
                  value={field.type}
                  onChange={(e) =>
                    updateField(cardIndex, fieldIndex, "type", e.target.value)
                  }
                >
                  <option value="text">Text</option>
                  <option value="select">Select</option>
                  <option value="radio">Radio</option>
                  <option value="checkbox">Checkbox</option>
                  <option value="date">Date</option>
                </select>
                {(field.type === "select" || field.type === "radio") && (
                  <div className="select-options">
                    <label>Options (comma-separated):</label>
                    <input
                      type="text"
                      value={field.options.join(",")}
                      onChange={(e) =>
                        updateField(
                          cardIndex,
                          fieldIndex,
                          "options",
                          e.target.value.split(",")
                        )
                      }
                    />
                  </div>
                )}
                <button
                  onClick={() => removeField(cardIndex, fieldIndex)}
                  className="remove-field-button"
                >
                  Remove Field
                </button>
              </div>
            ))}

            {/* Buttons to add fields */}
            <div className="add-field-buttons">
              <button onClick={() => addField(cardIndex, "text")}>
                + Text Field
              </button>
              <button onClick={() => addField(cardIndex, "select")}>
                + Select Field
              </button>
              <button onClick={() => addField(cardIndex, "radio")}>
                + Radio Field
              </button>
              <button onClick={() => addField(cardIndex, "checkbox")}>
                + Checkbox Field
              </button>
              <button onClick={() => addField(cardIndex, "date")}>
                + Date Field
              </button>
            </div>

            {/* Buttons that appear on hover at the bottom of the card */}
            <div className="card-hover-buttons bottom-buttons">
              {/* Add Card Below */}
              <button
                className="hover-button"
                onClick={() => addCardAtIndex(cardIndex, "below")}
              >
                + Card Below
              </button>
            </div>
          </div>
        ))}

        {/* Add a new card at the very end */}
        <button onClick={addCard} className="add-card-button">
          + Add Card
        </button>

        {/* Save/Update */}
        <button onClick={saveReport} className="save-report-button">
          {formId ? "Update Form" : "Save Form"}
        </button>

        {/* Preview (optional) */}
        <h2>Preview</h2>
        {cards.map((card) => (
          <div key={card.id} className="preview-card">
            <h3>
              {card.label} (Page {card.pageIndex || 1})
            </h3>
            {card.fields.map((field) => (
              <div key={field.id} className="preview-field">
                <label>{field.label}</label>
                {field.type === "text" && <input type="text" disabled />}
                {(field.type === "select" || field.type === "radio") && (
                  <select disabled>
                    {field.options.map((opt, idx) => (
                      <option key={idx}>{opt.trim()}</option>
                    ))}
                  </select>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default DynamicReport;
